import { z } from "zod";

/**
 * Les viewers, la renommee et les rangs (ticket 2 du socle, plan `docs/plans/2026-10-31-castellan-viewers.md`).
 * L'echelle des rangs est celle de L'Ecuyer (`renommee.rangs` de `config/serveur.json` dans la-kavalry),
 * recopiee une fois : un test l'epingle, pour qu'un changement soit voulu. Les deux cotes la lisent par leur
 * pont `contract.ts`.
 *
 * Aucun identifiant n'est un nombre : un `bigint` de la base voyage en texte. Les dates restent en ISO 8601.
 */

/** L'echelle des rangs, du plus bas au plus haut. `seuil` : les points de renommee qu'il faut. */
export const RANGS = [
  { id: "ecuyer", nom: "Écuyer", seuil: 0, couleur: "#79569A" },
  { id: "chevalier", nom: "Chevalier", seuil: 50, couleur: "#7FA0C8" },
  { id: "paladin", nom: "Paladin", seuil: 150, couleur: "#F09A5B" },
  { id: "heros", nom: "Héros", seuil: 300, couleur: "#C4566B" },
  { id: "seigneur", nom: "Seigneur", seuil: 600, couleur: "#9A4170" },
  { id: "legende", nom: "Légende", seuil: 1000, couleur: "#FCDD8E" },
] as const;

export type RangId = (typeof RANGS)[number]["id"];

/** Un rang de la liste, et rien d'autre. */
export const rangIdSchema = z.enum([
  "ecuyer",
  "chevalier",
  "paladin",
  "heros",
  "seigneur",
  "legende",
]);

/** La renommee d'un viewer sur sa fiche (`ficheViewer`) : `null` pour un viewer sans profil. */
export const renommeeFicheSchema = z.object({
  rang: rangIdSchema,
  renommee: z.number().int(),
  prochain: z
    .object({ rang: rangIdSchema, seuil: z.number().int(), reste: z.number().int() })
    .nullable(),
  /** Le rang que la taille de sa chaine lui garantit, ou `null` sans chaine lue. */
  plancher: rangIdSchema.nullable(),
  /** Les points par motif (`stream`, `messages`, `sub`...). */
  detail: z.record(z.string(), z.number().int()),
  /** Les stats des cartes (decision 28 de la spec). */
  stats: z.object({
    passages: z.number().int(),
    messages: z.number().int(),
    moisDeSub: z.number().int(),
    raids: z.number().int(),
  }),
  discord: z.object({ pseudo: z.string(), relieLe: z.string() }).nullable(),
});
export type RenommeeFiche = z.infer<typeof renommeeFicheSchema>;

/** `GET /api/viewers/classement?page=1&recherche=` : cinquante profils par page, ex aequo a la meme place. */
export const classementQuerySchema = z.object({
  page: z.coerce.number().int().min(1).max(100_000).default(1),
  recherche: z.string().trim().max(64).optional(),
});
export type ClassementQuery = z.infer<typeof classementQuerySchema>;

export const classementSchema = z.object({
  profils: z.array(
    z.object({
      profilId: z.string(),
      nom: z.string(),
      viewerId: z.string().nullable(),
      avatar: z.string().nullable(),
      discordPseudo: z.string().nullable(),
      rang: rangIdSchema,
      renommee: z.number().int(),
      position: z.number().int(),
    }),
  ),
  total: z.number().int(),
});
export type Classement = z.infer<typeof classementSchema>;
